// Copyright (c) Bentley Systems
import * as React from "react";
import { flushSync } from "react-dom";
import { EtNode } from "../DataModels/et-node";
import { TreeNodeData } from "../DataModels/tree-node-data";
import { ActionVm } from "../../Action/action-vm";
import {
  getErrorMessageFromResponse,
  getJsonFromParameters,
  getParametersFromUrlString,
  getUrlBasedOnEnv,
} from "../../Helpers/helper-functions";
import { Parameter } from "../../Action/parameter";
import {
  computeEffectiveExpanded,
  computePolicyCandidates,
  findNodeById,
  getRecordValue,
  mapRawNodeFields,
  type RawNodeFieldNames,
  toSafeString,
  TreeNodeDataWithRaw,
  TreeNodeRawData,
} from "../etree-utils";

// ---------------------------------------------------------------------------
// Refresh reason enum
// ---------------------------------------------------------------------------

export type RefreshReason = 'search' | 'action' | 'manual' | 'expansion' | 'panel-close';

const MAX_EAGER_REFRESH_NODES = 60;
const MAX_REFRESH_PARALLEL_REQUESTS = 6;

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export type UseEtreeDataResult = {
  treeData: TreeNodeData[];
  isLoading: boolean;
  error: string | null;
  expandedNodes: Record<string, boolean>;
  loadingNodes: Record<string, boolean>;
  appliedSearchTerm: string;
  urlParameters: Parameter[];
  policyExpandedCandidates: Record<string, boolean>;
  manualExpanded: Record<string, boolean>;
  manualCollapsed: Record<string, boolean>;
  refreshTree: (searchTermOverride?: string, reason?: RefreshReason) => Promise<void>;
  restoreExpandedNodes: (expandedNodesToRestore: Record<string, boolean>) => void;
  restoreExpansionIntent: (policy: Record<string, boolean>, manual: Record<string, boolean>, collapsed: Record<string, boolean>) => void;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setAppliedSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  fetchTreeData: (
    config: EtNode,
    parentId?: string,
    preserveExpandedState?: boolean,
    actionsMap?: Map<number, ActionVm>,
    externalSignal?: AbortSignal,
    options?: { ignoreSearchForChildren?: boolean; searchTermOverride?: string }
  ) => Promise<TreeNodeData[]>;
  onNodeExpanded: (nodeId: string, isExpanded: boolean) => Promise<void>;
  getNode: (node: TreeNodeData) => {
    subNodes: TreeNodeData[];
    nodeId: string;
    node: TreeNodeData;
    isExpanded: boolean;
    hasSubNodes: boolean;
  };
};

type UseEtreeDataProps = {
  treeConfig: EtNode | null;
  accessToken: string;
  urlParams: string | undefined;
  iTwinQueryParam: string | null;
  treeContextKey: string;
  itemActionsMap: Map<number, ActionVm>;
};

// ---------------------------------------------------------------------------
// Abort helper
// ---------------------------------------------------------------------------

const isAbortError = (err: unknown): boolean =>
  err instanceof DOMException && err.name === "AbortError";

const logEtreeDebug = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[Etree/useEtreeData]", ...args);
  }
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages tree data loading, expansion state, and search filtering.
 *
 * Handles:
 * - Fetching hierarchical tree data from the API with AbortController cancellation
 * - Managing expanded/collapsed node state via a policy + manual intent model
 * - Deduplication of nodes and search term filtering
 * - Cancellation of all in-flight requests on context change and unmount
 *
 * @param treeConfig - Active tree configuration (actions, object types, expand level, etc.)
 * @param accessToken - Bearer token for API authentication
 * @param urlParams - URL query parameter string passed to API requests
 * @param treeContextKey - Unique key identifying the current tree context; changing this resets all state
 * @param itemActionsMap - Map of actionId → ActionVm for resolving visible node actions
 * @returns Hook result with tree data, loading/error states, expansion helpers, and refresh methods
 */
export const useEtreeData = ({
  treeConfig,
  accessToken,
  urlParams,
  iTwinQueryParam,
  treeContextKey,
  itemActionsMap,
}: UseEtreeDataProps): UseEtreeDataResult => {
  const [treeData, setTreeData] = React.useState<TreeNodeData[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = React.useState<Record<string, boolean>>({});
  const [loadingNodes, setLoadingNodes] = React.useState<Record<string, boolean>>({});
  const [appliedSearchTerm, setAppliedSearchTerm] = React.useState<string>("");

  // Expansion-intent model state
  const [policyExpandedCandidates, setPolicyExpandedCandidates] = React.useState<Record<string, boolean>>({});
  const [manualExpanded, setManualExpanded] = React.useState<Record<string, boolean>>({});
  const [manualCollapsed, setManualCollapsed] = React.useState<Record<string, boolean>>({});
  const manualExpandedRef = React.useRef<Record<string, boolean>>({});
  const manualCollapsedRef = React.useRef<Record<string, boolean>>({});

  React.useEffect(() => {
    manualExpandedRef.current = manualExpanded;
  }, [manualExpanded]);

  React.useEffect(() => {
    manualCollapsedRef.current = manualCollapsed;
  }, [manualCollapsed]);

  const treeDataRef = React.useRef<TreeNodeData[]>([]);
  React.useEffect(() => {
    treeDataRef.current = treeData;
  }, [treeData]);

  const expandedNodesRef = React.useRef<Record<string, boolean>>({});
  React.useEffect(() => {
    expandedNodesRef.current = expandedNodes;
  }, [expandedNodes]);

  const loadingNodesRef = React.useRef<Record<string, boolean>>({});
  React.useEffect(() => {
    loadingNodesRef.current = loadingNodes;
  }, [loadingNodes]);

  const appliedSearchTermRef = React.useRef<string>("");
  React.useEffect(() => {
    appliedSearchTermRef.current = appliedSearchTerm;
  }, [appliedSearchTerm]);

  const urlParameters = React.useMemo(
    () => getParametersFromUrlString(urlParams),
    [urlParams]
  );
  const urlParametersRef = React.useRef<Parameter[]>([]);
  React.useEffect(() => {
    urlParametersRef.current = urlParameters;
  }, [urlParameters]);

  const refreshAbortControllerRef = React.useRef<AbortController | null>(null);
  const treeDataAbortControllersRef = React.useRef<Set<AbortController>>(new Set());
  const nodeLoadPromisesRef = React.useRef<Map<string, Promise<TreeNodeData[]>>>(new Map());
  const treeContextVersionRef = React.useRef<number>(0);

  const abortInFlightRequests = React.useCallback(() => {
    refreshAbortControllerRef.current?.abort();
    refreshAbortControllerRef.current = null;
    treeDataAbortControllersRef.current.forEach((controller) => controller.abort());
    treeDataAbortControllersRef.current.clear();
    nodeLoadPromisesRef.current.clear();
  }, []);

  React.useEffect(() => {
    logEtreeDebug("treeContextKey changed -> reset state", {
      treeContextKey,
      prevAppliedSearchTerm: appliedSearchTermRef.current,
    });

    treeContextVersionRef.current += 1;

    abortInFlightRequests();

    treeDataRef.current = [];
    expandedNodesRef.current = {};
    loadingNodesRef.current = {};
    manualExpandedRef.current = {};
    manualCollapsedRef.current = {};
    setTreeData([]);
    setExpandedNodes({});
    setLoadingNodes({});
    setPolicyExpandedCandidates({});
    setManualExpanded({});
    setManualCollapsed({});
    setError(null);
    setIsLoading(true);
  }, [treeContextKey, abortInFlightRequests]);

  React.useEffect(() => {
    return () => {
      abortInFlightRequests();
    };
  }, [abortInFlightRequests]);

  // ---------------------------------------------------------------------------
  // URL builder
  // ---------------------------------------------------------------------------

  const buildTreeDataUrl = React.useCallback(
    (nodeId: number, parentId?: string, searchTerm?: string): string => {
      let url = getUrlBasedOnEnv(`/api/Tree/GetTreeData/${nodeId}`);
      const queryParams: string[] = [];
      if (parentId) queryParams.push(`parentId=${encodeURIComponent(parentId)}`);
      const trimmed = searchTerm?.trim();
      if (trimmed) queryParams.push(`searchTerm=${encodeURIComponent(trimmed)}`);
      if (queryParams.length > 0) url += `?${queryParams.join("&")}`;
      return url;
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Tree transformation
  // ---------------------------------------------------------------------------

  type TreeNodeDataWithTemp = TreeNodeData & {
    _parentId?: string;
    _level?: number;
    rawData?: TreeNodeRawData;
  };

  const buildHierarchicalTree = React.useCallback(
    (
      data: TreeNodeRawData[] | TreeNodeRawData,
      config: EtNode,
      actionsMap: Map<number, ActionVm>
    ): { treeData: TreeNodeData[]; expandedNodes: Record<string, boolean> } => {
      const dataArray = Array.isArray(data) ? data : [data];
      if (!dataArray.length) return { treeData: [], expandedNodes: {} };

      if (!config.etParentChildTreeType) {
        setError("Invalid tree configuration: etParentChildTreeType is missing.");
        return { treeData: [], expandedNodes: {} };
      }

      const { childIdfield, parentIdfield, childNameField, childObjectTypeField, childSublabelField } =
        config.etParentChildTreeType;
      const fieldNames: RawNodeFieldNames = {
        childNameField,
        childSublabelField: childSublabelField ?? "",
        childObjectTypeField,
      };

      const allNodes: TreeNodeDataWithTemp[] = [];
      const seenNodeIds = new Set<string>();
      let duplicateNodeCount = 0;

      dataArray.forEach((item: TreeNodeRawData, index: number) => {
        const rawItemId = item[childIdfield];
        const normalizedRawItemId = toSafeString(rawItemId);
        const itemId =
          normalizedRawItemId !== undefined && normalizedRawItemId !== ""
            ? normalizedRawItemId
            : `node-${index}`;
        let parentId: unknown = item._parentId;

        if (seenNodeIds.has(itemId)) {
          duplicateNodeCount += 1;
          return;
        }
        seenNodeIds.add(itemId);

        if (parentId !== undefined && parentId !== null && parentId !== "") {
          parentId = toSafeString(parentId) || "";
        } else {
          const cfgParent = getRecordValue(item, parentIdfield);
          const normalizedParentId = toSafeString(cfgParent);
          parentId =
            normalizedParentId !== undefined && normalizedParentId !== ""
              ? normalizedParentId
              : "";
        }

        const levelVal = item._level;
        const level = typeof levelVal === "number" ? levelVal : 1;

        const { label, sublabel, hasChildren, objectTypes, actionIds, icon, iconBase64Str } =
          mapRawNodeFields(item, index, fieldNames, config, actionsMap);

        allNodes.push({
          id: String(itemId),
          label,
          sublabel,
          objectTypes,
          icon,
          iconBase64Str,
          actionIds,
          subItems: [],
          hasChildren,
          isLoaded: false,
          loadedWithoutSearch: false,
          _parentId: typeof parentId === "string" ? parentId : undefined,
          _level: level,
          rawData: item,
        });
      });

      if (duplicateNodeCount > 0) {
        logEtreeDebug("buildHierarchicalTree deduped duplicate node IDs", {
          duplicateNodeCount,
          totalInputCount: dataArray.length,
          dedupedCount: allNodes.length,
        });
      }

      const nodeMap = new Map<string, TreeNodeDataWithTemp>();
      const rootNodes: TreeNodeDataWithTemp[] = [];
      const autoExpanded: Record<string, boolean> = {};

      allNodes.forEach((node) => nodeMap.set(node.id, node));

      allNodes.forEach((node) => {
        const pid = node._parentId ?? "";
        const nodeLevel = node._level ?? 1;

        if (pid === "" || pid === "0") {
          rootNodes.push(node);
        } else {
          const parent = nodeMap.get(pid);
          if (parent) parent.subItems.push(node);
          else rootNodes.push(node);
        }

        node.isLoaded = node.subItems.length > 0 || !node.hasChildren;

        if (config.expandLevel && nodeLevel < config.expandLevel) {
          autoExpanded[node.id] = true;
        }
      });

      allNodes.forEach((node) => {
        delete node._parentId;
        delete node._level;
      });

      return { treeData: rootNodes, expandedNodes: autoExpanded };
    },
    []
  );

  const transformTreeData = React.useCallback(
    (
      data: TreeNodeRawData[] | TreeNodeRawData,
      config: EtNode,
      actionsMap: Map<number, ActionVm>
    ): TreeNodeData[] => {
      const dataArray = Array.isArray(data) ? data : [data];
      if (!dataArray.length) return [];

      if (!config.etParentChildTreeType) {
        setError("Invalid tree configuration: etParentChildTreeType is missing.");
        return [];
      }

      const { childIdfield, childNameField, childObjectTypeField, childSublabelField } =
        config.etParentChildTreeType;
      const fieldNames: RawNodeFieldNames = {
        childNameField,
        childSublabelField: childSublabelField ?? "",
        childObjectTypeField,
      };

      const mappedNodes = dataArray.map((item: TreeNodeRawData, index: number): TreeNodeDataWithRaw => {
        const normalizedItemId = toSafeString(item[childIdfield]);

        const { label, sublabel, hasChildren, objectTypes, actionIds, icon, iconBase64Str } =
          mapRawNodeFields(item, index, fieldNames, config, actionsMap);

        return {
          id: normalizedItemId || `node-${index}`,
          label,
          sublabel,
          objectTypes,
          icon,
          iconBase64Str,
          actionIds,
          subItems: [],
          hasChildren,
          isLoaded: !hasChildren,
          loadedWithoutSearch: !hasChildren,
          rawData: item,
        };
      });

      const seenNodeIds = new Set<string>();
      return mappedNodes.filter((node) => {
        if (seenNodeIds.has(node.id)) return false;
        seenNodeIds.add(node.id);
        return true;
      });
    },
    []
  );

  const dedupeTreeById = React.useCallback((nodes: TreeNodeData[]): TreeNodeData[] => {
    const seenNodeIds = new Set<string>();
    let duplicateNodeCount = 0;

    const walk = (currentNodes: TreeNodeData[]): TreeNodeData[] =>
      currentNodes.reduce<TreeNodeData[]>((acc, currentNode) => {
        if (seenNodeIds.has(currentNode.id)) {
          duplicateNodeCount += 1;
          return acc;
        }

        seenNodeIds.add(currentNode.id);

        const dedupedSubItems =
          currentNode.subItems.length > 0 ? walk(currentNode.subItems) : currentNode.subItems;

        if (dedupedSubItems !== currentNode.subItems) {
          acc.push({ ...currentNode, subItems: dedupedSubItems });
        } else {
          acc.push(currentNode);
        }

        return acc;
      }, []);

    const dedupedTree = walk(nodes);

    if (duplicateNodeCount > 0) {
      logEtreeDebug("dedupeTreeById removed duplicate node IDs", {
        duplicateNodeCount,
      });
    }

    return dedupedTree;
  }, []);

  const updateTreeWithChildren = React.useCallback(
    (parentId: string, children: TreeNodeData[], loadedWithoutSearch = false) => {
      const updateNode = (nodes: TreeNodeData[]): TreeNodeData[] =>
        nodes.map((node) => {
          if (node.id === parentId) {
            return { ...node, subItems: children, isLoaded: true, loadedWithoutSearch };
          }
          if (node.subItems.length > 0) {
            return { ...node, subItems: updateNode(node.subItems) };
          }
          return node;
        });
      setTreeData((prev) => {
        const next = dedupeTreeById(updateNode(prev));
        treeDataRef.current = next;
        return next;
      });
    },
    [dedupeTreeById]
  );

  // ---------------------------------------------------------------------------
  // fetchTreeData
  // ---------------------------------------------------------------------------

  const fetchTreeData = React.useCallback(
    async (
      config: EtNode,
      parentId?: string,
      preserveExpandedState = false,
      actionsMap?: Map<number, ActionVm>,
      externalSignal?: AbortSignal,
      options?: { ignoreSearchForChildren?: boolean; searchTermOverride?: string }
    ): Promise<TreeNodeData[]> => {
      // If a full refresh is already in progress, skip root-level data loads to prevent race.
      if (!parentId && refreshAbortControllerRef.current !== null) {
        return [];
      }

      const requestContextVersion = treeContextVersionRef.current;
      const isStaleRequest = () => requestContextVersion !== treeContextVersionRef.current;

      let localController: AbortController | undefined;
      const signal =
        externalSignal ||
        (() => {
          localController = new AbortController();
          treeDataAbortControllersRef.current.add(localController);
          return localController.signal;
        })();

      try {
        const searchTermToUse =
          options?.searchTermOverride !== undefined
            ? options.searchTermOverride
            : parentId && options?.ignoreSearchForChildren
              ? ""
              : appliedSearchTermRef.current;

        logEtreeDebug("fetchTreeData request", {
          nodeId: config.nodeId,
          parentId,
          searchTermToUse,
          searchTermOverride: options?.searchTermOverride,
          ignoreSearchForChildren: options?.ignoreSearchForChildren,
          requestContextVersion,
        });

        const loadedWithoutSearch = !searchTermToUse.trim();

        const url = buildTreeDataUrl(config.nodeId, parentId, searchTermToUse);
        const jsonBody = getJsonFromParameters(urlParametersRef.current);

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: crypto.randomUUID(),
            ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
          },
          signal,
          body: jsonBody,
        });

        if (isStaleRequest()) return [];

        if (!response.ok) {
          const message = await getErrorMessageFromResponse(response);
          if (isStaleRequest()) return [];
          setError(message);
          return [];
        }

        const result = (await response.json()) as TreeNodeRawData[];

        if (isStaleRequest()) return [];

        const effectiveActionsMap = actionsMap || itemActionsMap;
        if (parentId) {
          const children = transformTreeData(result, config, effectiveActionsMap);
          const childrenWithLoadSource = children.map((child) => ({
            ...child,
            loadedWithoutSearch,
          }));
          updateTreeWithChildren(parentId, childrenWithLoadSource, loadedWithoutSearch);
          return childrenWithLoadSource;
        } else {
          const { treeData: hierarchical, expandedNodes: autoExpanded } = buildHierarchicalTree(
            result,
            config,
            effectiveActionsMap
          );
          const dedupedHierarchy = dedupeTreeById(hierarchical);
          treeDataRef.current = dedupedHierarchy;
          setTreeData(dedupedHierarchy);
          if (!preserveExpandedState) setExpandedNodes(autoExpanded);
          return dedupedHierarchy;
        }
      } catch (err) {
        if (isAbortError(err)) return [];
        if (isStaleRequest()) return [];
        setError(err instanceof Error ? err.message : "Failed to load tree data");
        return [];
      } finally {
        if (localController) treeDataAbortControllersRef.current.delete(localController);
        if (!isStaleRequest()) {
          setIsLoading(false);
          if (parentId) {
            loadingNodesRef.current = { ...loadingNodesRef.current, [parentId]: false };
            setLoadingNodes((prev) => ({ ...prev, [parentId]: false }));
          }
        }
      }
    },
    [
      accessToken,
      iTwinQueryParam,
      itemActionsMap,
      buildTreeDataUrl,
      buildHierarchicalTree,
      transformTreeData,
      dedupeTreeById,
      updateTreeWithChildren,
    ]
  );

  // ---------------------------------------------------------------------------
  // refreshTree
  // ---------------------------------------------------------------------------

  const refreshTree = React.useCallback(
    async (searchTermOverride?: string, reason: RefreshReason = 'manual') => {
      if (!treeConfig) return;

      const requestContextVersion = treeContextVersionRef.current;
      const isStaleRequest = () => requestContextVersion !== treeContextVersionRef.current;

      refreshAbortControllerRef.current?.abort();
      const refreshAbortController = new AbortController();
      refreshAbortControllerRef.current = refreshAbortController;

      const currentExpandedNodes = { ...expandedNodesRef.current };

      setIsLoading(true);
      setError(null);

      try {
        const effectiveSearchTerm = searchTermOverride ?? appliedSearchTermRef.current;
        logEtreeDebug("refreshTree start", {
          reason,
          searchTermOverride,
          appliedSearchTerm: appliedSearchTermRef.current,
          effectiveSearchTerm,
          requestContextVersion,
        });

        const isExplicitSearchClear =
          searchTermOverride !== undefined && !searchTermOverride.trim();
        const isSearchActive = Boolean(effectiveSearchTerm?.trim());
        const shouldResetSearchSession =
          isSearchActive && (reason === 'panel-close' || reason === 'action');
        const isFreshSearchLoad = (reason === 'search' && isSearchActive) || shouldResetSearchSession;
        const isInitialStyleDefaultLoad =
          isExplicitSearchClear || (!effectiveSearchTerm.trim() && reason === 'manual');
        const shouldResetToAutoExpanded =
          !effectiveSearchTerm.trim() &&
          (isExplicitSearchClear || Boolean(appliedSearchTermRef.current.trim()));
        const jsonBody = getJsonFromParameters(urlParametersRef.current);

        const rootUrl = buildTreeDataUrl(treeConfig.nodeId, undefined, effectiveSearchTerm);
        const rootResponse = await fetch(rootUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: crypto.randomUUID(),
            ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
          },
          signal: refreshAbortController.signal,
          body: jsonBody,
        });

        if (isStaleRequest()) return;

        if (!rootResponse.ok) {
          const message = await getErrorMessageFromResponse(rootResponse);
          if (isStaleRequest()) return;
          setError(message);
          treeDataRef.current = [];
          setTreeData([]);
          setIsLoading(false);
          return;
        }

        const rootData = (await rootResponse.json()) as TreeNodeRawData[];
        if (isStaleRequest()) return;

        {
          const { treeData: freshRootData, expandedNodes: freshAutoExpanded } = buildHierarchicalTree(
            rootData,
            treeConfig,
            itemActionsMap
          );
          const childLoadErrors: string[] = [];

          const freshPolicyCandidates = treeConfig.expandLevel
            ? computePolicyCandidates(freshRootData, treeConfig.expandLevel)
            : {};

          let targetExpandedNodes: Record<string, boolean> = {};

          if (reason === 'search' || shouldResetSearchSession) {
            if (shouldResetSearchSession) {
              manualExpandedRef.current = {};
              manualCollapsedRef.current = {};
              setManualExpanded({});
              setManualCollapsed({});
            }

            const searchExpandedNodes: Record<string, boolean> = {};
            const markExpandableSearchNodes = (nodes: TreeNodeData[]) => {
              for (const node of nodes) {
                if (node.subItems.length > 0) {
                  searchExpandedNodes[node.id] = true;
                  markExpandableSearchNodes(node.subItems);
                }
              }
            };
            markExpandableSearchNodes(freshRootData);
            targetExpandedNodes = computeEffectiveExpanded(
              searchExpandedNodes,
              manualExpandedRef.current,
              manualCollapsedRef.current,
              freshRootData
            );
            setPolicyExpandedCandidates(searchExpandedNodes);
          } else if (isInitialStyleDefaultLoad) {
            manualExpandedRef.current = {};
            manualCollapsedRef.current = {};
            setManualExpanded({});
            setManualCollapsed({});
            setPolicyExpandedCandidates(freshAutoExpanded);
            targetExpandedNodes = freshAutoExpanded;
          } else if (reason === 'panel-close') {
            targetExpandedNodes = currentExpandedNodes;
            setPolicyExpandedCandidates(freshPolicyCandidates);
          } else if (reason === 'action') {
            if (isSearchActive) {
              targetExpandedNodes = currentExpandedNodes;
            } else {
              const effectiveExpanded = computeEffectiveExpanded(
                freshPolicyCandidates,
                manualExpandedRef.current,
                manualCollapsedRef.current,
                freshRootData
              );
              targetExpandedNodes = effectiveExpanded;
            }
            setPolicyExpandedCandidates(freshPolicyCandidates);
          } else if (shouldResetToAutoExpanded) {
            setPolicyExpandedCandidates(freshAutoExpanded);
            targetExpandedNodes = freshAutoExpanded;
          } else {
            targetExpandedNodes = currentExpandedNodes;
          }

          const hasExpandedNodes = Object.values(targetExpandedNodes).some((isExpanded) => isExpanded);
          const shouldReloadExpandedChildren =
            hasExpandedNodes && !isFreshSearchLoad && !isInitialStyleDefaultLoad;
          if (shouldReloadExpandedChildren) {
            const visitedExpandedNodeIds = new Set<string>();
            const isExpandedAndLoadable = (node: TreeNodeData): boolean =>
              targetExpandedNodes[node.id] === true && node.hasChildren;

            let processedExpandedNodeCount = 0;
            let waveNodes = freshRootData.filter(isExpandedAndLoadable);

            while (waveNodes.length > 0 && processedExpandedNodeCount < MAX_EAGER_REFRESH_NODES) {
              const remainingBudget = MAX_EAGER_REFRESH_NODES - processedExpandedNodeCount;
              const currentWave = waveNodes
                .filter((node) => !visitedExpandedNodeIds.has(node.id))
                .slice(0, remainingBudget);

              if (currentWave.length === 0) {
                break;
              }

              currentWave.forEach((node) => visitedExpandedNodeIds.add(node.id));
              processedExpandedNodeCount += currentWave.length;

              const nextWaveCandidates: TreeNodeData[] = [];

              for (let index = 0; index < currentWave.length; index += MAX_REFRESH_PARALLEL_REQUESTS) {
                const chunk = currentWave.slice(index, index + MAX_REFRESH_PARALLEL_REQUESTS);

                await Promise.all(
                  chunk.map(async (node) => {
                    try {
                      const childSearchTerm = effectiveSearchTerm;
                      const childUrl = buildTreeDataUrl(
                        treeConfig.nodeId,
                        node.id,
                        childSearchTerm
                      );
                      const childResponse = await fetch(childUrl, {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: accessToken,
                          RunId: crypto.randomUUID(),
                          ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
                        },
                        signal: refreshAbortController.signal,
                        body: jsonBody,
                      });

                      if (isStaleRequest()) return;

                      if (!childResponse.ok) {
                        const message = await getErrorMessageFromResponse(childResponse);
                        childLoadErrors.push(`Node ${node.id}: ${message}`);
                        return;
                      }

                      const childRawData = (await childResponse.json()) as TreeNodeRawData[];

                      const childData = transformTreeData(
                        childRawData,
                        treeConfig,
                        itemActionsMap
                      );

                      node.subItems = childData;
                      node.isLoaded = true;
                      node.loadedWithoutSearch = !childSearchTerm.trim();

                      if (childData.length > 0) {
                        nextWaveCandidates.push(...childData);
                      }
                    } catch (err) {
                      if (isAbortError(err)) throw err;
                      childLoadErrors.push(
                        `Node ${node.id}: ${err instanceof Error ? err.message : "Failed to load child nodes"}`
                      );
                    }
                  })
                );
              }

              waveNodes = nextWaveCandidates.filter(
                (node) => isExpandedAndLoadable(node) && !visitedExpandedNodeIds.has(node.id)
              );
            }

            if (processedExpandedNodeCount >= MAX_EAGER_REFRESH_NODES) {
              logEtreeDebug("refreshTree bounded expanded reload cap reached", {
                maxEagerRefreshNodes: MAX_EAGER_REFRESH_NODES,
              });
            }
          }

          flushSync(() => {
            if (isStaleRequest()) return;
            const dedupedTree = dedupeTreeById(freshRootData);
            treeDataRef.current = dedupedTree;
            setTreeData(dedupedTree);
            setExpandedNodes(targetExpandedNodes);
          });

          if (isStaleRequest()) return;
          setError(
            childLoadErrors.length > 0
              ? `Some expanded nodes could not be refreshed. ${childLoadErrors.slice(0, 3).join(" | ")}`
              : null
          );
          setIsLoading(false);
        }
      } catch (err) {
        if (!isAbortError(err) && !isStaleRequest()) {
          setError(err instanceof Error ? err.message : "Failed to refresh tree data");
          treeDataRef.current = [];
          setTreeData([]);
          setIsLoading(false);
        }
      } finally {
        if (!isStaleRequest() && refreshAbortControllerRef.current === refreshAbortController) {
          refreshAbortControllerRef.current = null;
        }
      }
    },
    [
      treeConfig,
      accessToken,
      iTwinQueryParam,
      buildTreeDataUrl,
      buildHierarchicalTree,
      transformTreeData,
      dedupeTreeById,
      itemActionsMap,
    ]
  );

  // ---------------------------------------------------------------------------
  // Helper — clear descendant expanded state on collapse
  // ---------------------------------------------------------------------------

  const clearDescendantExpandedState = React.useCallback(
    (nodeId: string, treeDataToSearch: TreeNodeData[]): string[] => {
      const descendantIds: string[] = [];
      const node = findNodeById(treeDataToSearch, nodeId);
      if (!node) return descendantIds;

      const collectDescendants = (n: TreeNodeData) => {
        if (n.subItems && n.subItems.length > 0) {
          for (const child of n.subItems) {
            descendantIds.push(child.id);
            collectDescendants(child);
          }
        }
      };

      collectDescendants(node);
      return descendantIds;
    },
    []
  );

  // ---------------------------------------------------------------------------
  // onNodeExpanded
  // ---------------------------------------------------------------------------

  const onNodeExpanded = React.useCallback(
    async (nodeId: string, isExpanded: boolean) => {
      logEtreeDebug("onNodeExpanded", {
        nodeId,
        isExpanded,
        appliedSearchTerm: appliedSearchTermRef.current,
      });

      if (isExpanded) {
        const nextManualExpanded = { ...manualExpandedRef.current, [nodeId]: true };
        manualExpandedRef.current = nextManualExpanded;
        setManualExpanded(nextManualExpanded);

        const nextManualCollapsed = { ...manualCollapsedRef.current };
        delete nextManualCollapsed[nodeId];
        manualCollapsedRef.current = nextManualCollapsed;
        setManualCollapsed(nextManualCollapsed);

        setExpandedNodes((prev) => {
          const next = { ...prev, [nodeId]: isExpanded };
          expandedNodesRef.current = next;
          return next;
        });

        if (treeConfig) {
          const node = findNodeById(treeDataRef.current, nodeId);
          const isSearchActive = Boolean(appliedSearchTermRef.current.trim());
          const shouldLoadUnfilteredChildren = Boolean(isSearchActive && !node?.loadedWithoutSearch);
          const shouldLazyLoad = Boolean(node && node.hasChildren && !node.isLoaded);

          if (
            node &&
            node.hasChildren &&
            !loadingNodesRef.current[nodeId] &&
            (shouldLazyLoad || shouldLoadUnfilteredChildren)
          ) {
            const existingLoadPromise = nodeLoadPromisesRef.current.get(nodeId);
            if (existingLoadPromise) {
              await existingLoadPromise;
              return;
            }

            if (shouldLoadUnfilteredChildren) {
              const descendantIds = clearDescendantExpandedState(nodeId, treeDataRef.current);
              setExpandedNodes((prev) => {
                const next = { ...prev, [nodeId]: true };
                for (const descendantId of descendantIds) {
                  next[descendantId] = false;
                }
                expandedNodesRef.current = next;
                return next;
              });

              setTreeData((prev) => {
                const clearNodeChildren = (nodes: TreeNodeData[]): TreeNodeData[] =>
                  nodes.map((currentNode) => {
                    if (currentNode.id === nodeId) {
                      return {
                        ...currentNode,
                        subItems: [],
                        isLoaded: false,
                        loadedWithoutSearch: false,
                      };
                    }

                    if (currentNode.subItems.length > 0) {
                      return {
                        ...currentNode,
                        subItems: clearNodeChildren(currentNode.subItems),
                      };
                    }

                    return currentNode;
                  });

                const next = clearNodeChildren(prev);
                treeDataRef.current = next;
                return next;
              });
            }

            loadingNodesRef.current = { ...loadingNodesRef.current, [nodeId]: true };
            setLoadingNodes((prev) => ({ ...prev, [nodeId]: true }));
            const nodeLoadPromise = fetchTreeData(treeConfig, nodeId, false, undefined, undefined, {
              ignoreSearchForChildren: isSearchActive,
            });
            nodeLoadPromisesRef.current.set(nodeId, nodeLoadPromise);

            try {
              await nodeLoadPromise;
            } finally {
              if (nodeLoadPromisesRef.current.get(nodeId) === nodeLoadPromise) {
                nodeLoadPromisesRef.current.delete(nodeId);
              }
            }
          }
        }
      } else {
        const nextManualCollapsed = { ...manualCollapsedRef.current, [nodeId]: true };
        manualCollapsedRef.current = nextManualCollapsed;
        setManualCollapsed(nextManualCollapsed);

        const nextManualExpanded = { ...manualExpandedRef.current };
        delete nextManualExpanded[nodeId];
        manualExpandedRef.current = nextManualExpanded;
        setManualExpanded(nextManualExpanded);

        const descendantIds = clearDescendantExpandedState(nodeId, treeDataRef.current);
        setExpandedNodes((prev) => {
          const next = { ...prev, [nodeId]: false };
          for (const descendantId of descendantIds) {
            next[descendantId] = false;
          }
          expandedNodesRef.current = next;
          return next;
        });
      }
    },
    [treeConfig, fetchTreeData, clearDescendantExpandedState]
  );

  // ---------------------------------------------------------------------------
  // getNode — iTwinUI Tree adapter
  // ---------------------------------------------------------------------------

  const getNode = React.useCallback(
    (node: TreeNodeData) => ({
      subNodes: node.subItems,
      nodeId: node.id,
      node,
      isExpanded: expandedNodes[node.id] ?? false,
      hasSubNodes: node.hasChildren || node.subItems.length > 0,
    }),
    [expandedNodes]
  );

  const restoreExpandedNodes = React.useCallback(
    (expandedNodesToRestore: Record<string, boolean>) => {
      expandedNodesRef.current = expandedNodesToRestore;
      setExpandedNodes(expandedNodesToRestore);
    },
    []
  );

  const restoreExpansionIntent = React.useCallback(
    (
      policy: Record<string, boolean>,
      manual: Record<string, boolean>,
      collapsed: Record<string, boolean>
    ) => {
      setPolicyExpandedCandidates(policy);
      manualExpandedRef.current = manual;
      manualCollapsedRef.current = collapsed;
      setManualExpanded(manual);
      setManualCollapsed(collapsed);
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  return {
    treeData,
    isLoading,
    error,
    expandedNodes,
    loadingNodes,
    appliedSearchTerm,
    urlParameters,
    policyExpandedCandidates,
    manualExpanded,
    manualCollapsed,
    refreshTree,
    restoreExpandedNodes,
    restoreExpansionIntent,
    setError,
    setAppliedSearchTerm,
    fetchTreeData,
    onNodeExpanded,
    getNode,
  };
};
